import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import SortableHeader, { useSort } from '../../components/SortableHeader'
import { fmtDate, fmtDateTime } from '../../utils/format'
import { norm } from '../../utils/normalize'
import {
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  TrashIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon,
  PrinterIcon
} from '@heroicons/react/24/outline'
import { useToast } from '../../contexts/ToastContext'

interface Supplier {
  id: number
  trade_name: string
  nif: string
  address?: string
  phone?: string
  email?: string
  is_self?: boolean
}

interface Unit {
  id: number
  name: string
  type: string
}

interface Ingredient {
  id: number
  name: string
  type: 'normal' | 'elaborated' | 'limpieza' | 'packaging' | 'finished_product' | 'pack'
  default_unit_id?: number
  default_unit_name?: string
  preferred_supplier_id?: number
  default_price_per_unit?: number
}

interface PurchaseOrderItem {
  id?: number
  ingredient_id: number
  ingredient?: Ingredient
  quantity: number
  unit_id: number
  unit?: Unit
  unit_price?: number
  notes?: string
}

interface PurchaseOrder {
  id: number
  code: string
  supplier_id?: number
  supplier?: Supplier
  status: 'draft' | 'sent' | 'partial' | 'received' | 'closed'
  items?: PurchaseOrderItem[]
  items_count?: number
  receipts_count?: number
  total_estimated_cost?: number
  notes?: string
  created_at: string
  updated_at: string
  sent_at?: string
  received_at?: string
}

const API_BASE_URL = '/api/v1/dev'

export default function PurchaseOrders() {
  const toast = useToast()
  const [orders, setOrders] = useState<PurchaseOrder[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [ingredients, setIngredients] = useState<Ingredient[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null)
  const [editingOrder, setEditingOrder] = useState<PurchaseOrder | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ticketsMode, setTicketsMode] = useState<string>('local')
  const [printing, setPrinting] = useState(false)

  // Form data
  const [formData, setFormData] = useState({
    supplier_id: '',
    notes: '',
    items: [] as PurchaseOrderItem[]
  })

  // Fetch purchase orders
  const fetchOrders = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(`${API_BASE_URL}/purchase-orders`)
      const data = await response.json()

      if (response.ok) {
        setOrders(data.data || [])
      } else {
        setError('Error al cargar órdenes de compra')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error fetching orders:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch suppliers (excluding self-supplier - can't buy from ourselves)
  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers`)
      const data = await response.json()
      if (response.ok) {
        const externalSuppliers = (data.data || []).filter((s: Supplier) => !s.is_self)
        setSuppliers(externalSuppliers)
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err)
    }
  }

  // Fetch ingredients (excluding in-house products - can't purchase them)
  const fetchIngredients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredients`)
      const data = await response.json()
      if (response.ok) {
        const purchasableTypes = ['normal', 'limpieza', 'packaging']
        const purchasableIngredients = (data.data || []).filter((i: Ingredient) => purchasableTypes.includes(i.type))
        setIngredients(purchasableIngredients)
      }
    } catch (err) {
      console.error('Error fetching ingredients:', err)
    }
  }

  // Fetch units
  const fetchUnits = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/units`)
      const data = await response.json()
      if (response.ok) {
        setUnits(data.data || [])
      }
    } catch (err) {
      console.error('Error fetching units:', err)
    }
  }

  // Modo de la impresora de tickets (local abre el PDF; ipp/cups lo envía)
  const fetchTicketsMode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/printers`, { headers: { Accept: 'application/json' } })
      const data = await response.json()
      if (response.ok && data.success) {
        const tickets = (data.data || []).find((p: any) => p.key === 'tickets')
        if (tickets) setTicketsMode(tickets.mode)
      }
    } catch (err) {
      console.error('Error fetching printers:', err)
    }
  }

  // Imprime la lista de compra: local abre el PDF; ipp/cups lo envía a la impresora
  const printPurchaseList = async () => {
    const id = selectedOrder?.id
    if (!id || printing) return
    if (ticketsMode === 'local') {
      window.open(`${API_BASE_URL}/purchase-orders/${id}/print`, '_blank')
      return
    }
    setPrinting(true)
    try {
      const res = await fetch(`${API_BASE_URL}/purchase-orders/${id}/print/send`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      })
      const data = await res.json()
      if (res.ok && data.success) toast.success(data.message || 'Enviado a la impresora')
      else toast.error(data.message || 'No se pudo imprimir')
    } catch {
      toast.error('Error de conexión con la impresora')
    } finally {
      setPrinting(false)
    }
  }

  useEffect(() => {
    fetchOrders()
    fetchSuppliers()
    fetchIngredients()
    fetchUnits()
    fetchTicketsMode()
  }, [])

  // Precarga de items al llegar desde "Generar OC de faltantes" (orden de producción)
  const location = useLocation()
  const navigate = useNavigate()
  const [prefillDone, setPrefillDone] = useState(false)

  useEffect(() => {
    const prefill = (location.state as any)?.prefillItems as
      Array<{ ingredient_id: number; quantity: number; unit_id: number }> | undefined
    if (!prefill || prefill.length === 0 || prefillDone) return
    if (ingredients.length === 0 || units.length === 0) return // esperar a que cargue

    const items: PurchaseOrderItem[] = prefill.map(p => {
      const ing = ingredients.find(i => i.id === p.ingredient_id)
      return {
        ingredient_id: p.ingredient_id,
        quantity: parseFloat(Number(p.quantity).toFixed(3)),
        unit_id: p.unit_id || ing?.default_unit_id || 0,
        unit_price: 0,
        notes: ''
      }
    })

    setEditingOrder(null)
    setFormData({ supplier_id: '', notes: '', items })
    setShowCreateModal(true)
    setError(null)
    setPrefillDone(true)
    // limpiar el state para no reabrir el modal al refrescar/navegar
    navigate(location.pathname, { replace: true, state: null })
  }, [location.state, ingredients, units, prefillDone])

  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const perPage = 15
  const { sortField, sortDirection, handleSort, sortData } = useSort<PurchaseOrder>('code')

  // Filter orders by status and search
  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter
    const matchesSearch = !searchTerm || norm(order.code).includes(norm(searchTerm)) ||
      norm(order.supplier?.trade_name || '').includes(norm(searchTerm))
    return matchesStatus && matchesSearch
  })

  const sortedOrders = sortData(filteredOrders)
  const totalPages = Math.ceil(sortedOrders.length / perPage)
  const paginatedOrders = sortedOrders.slice((currentPage - 1) * perPage, currentPage * perPage)

  useEffect(() => { setCurrentPage(1) }, [statusFilter, searchTerm])

  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: ClockIcon, text: 'Borrador' },
      sent: { color: 'bg-blue-100 text-blue-800', icon: PaperAirplaneIcon, text: 'Enviada' },
      partial: { color: 'bg-yellow-100 text-yellow-800', icon: DocumentTextIcon, text: 'Parcial' },
      received: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, text: 'Recibida' },
      closed: { color: 'bg-purple-100 text-purple-800', icon: CheckCircleIcon, text: 'Cerrada' }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {config.text}
      </span>
    )
  }

  // Add item to order
  const addItem = () => {
    setFormData({
      ...formData,
      items: [
        {
          ingredient_id: 0,
          quantity: 1,
          unit_id: 0,
          unit_price: 0,
          notes: ''
        },
        ...formData.items
      ]
    })
  }

  // Remove item from order
  const removeItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    })
  }

  // Update item in order
  const updateItem = (index: number, field: keyof PurchaseOrderItem, value: any) => {
    const updatedItems = [...formData.items]

    // If changing ingredient, auto-load its default unit
    if (field === 'ingredient_id') {
      const selectedIngredient = ingredients.find(ing => ing.id === value)
      if (selectedIngredient && selectedIngredient.default_unit_id) {
        updatedItems[index] = {
          ...updatedItems[index],
          ingredient_id: value,
          unit_id: selectedIngredient.default_unit_id
        }
      } else {
        updatedItems[index] = {
          ...updatedItems[index],
          [field]: value
        }
      }
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value
      }
    }

    setFormData({
      ...formData,
      items: updatedItems
    })
  }

  // Handle create or update order
  const handleCreateOrder = async () => {
    if (!formData.supplier_id) {
      setError('Debes seleccionar un proveedor')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const url = editingOrder
        ? `${API_BASE_URL}/purchase-orders/${editingOrder.id}`
        : `${API_BASE_URL}/purchase-orders`

      const method = editingOrder ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          supplier_id: formData.supplier_id || null,
          notes: formData.notes,
          items: formData.items.map(item => ({
            ingredient_id: item.ingredient_id,
            quantity: item.quantity,
            unit_id: item.unit_id,
            unit_price: item.unit_price || null,
            notes: item.notes || null
          }))
        }),
      })

      const data = await response.json()

      if (response.ok) {
        await fetchOrders()
        setShowCreateModal(false)
        setEditingOrder(null)
        setFormData({
          supplier_id: '',
          notes: '',
          items: []
        })
      } else {
        setError(data.message || 'Error al guardar la orden')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error saving order:', err)
    } finally {
      setSaving(false)
    }
  }

  // Send order
  const sendOrder = async (orderId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/purchase-orders/${orderId}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        await fetchOrders()
      } else {
        const data = await response.json()
        setError(data.message || 'Error al enviar la orden')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error sending order:', err)
    }
  }

  // Delete order
  const deleteOrder = async (orderId: number) => {
    if (!confirm('¿Está seguro de eliminar esta orden de compra?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/purchase-orders/${orderId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        await fetchOrders()
      } else {
        const data = await response.json()
        setError(data.message || 'Error al eliminar la orden')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error deleting order:', err)
    }
  }

  // View order details
  const viewOrderDetails = async (orderId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/purchase-orders/${orderId}`)
      const data = await response.json()

      if (response.ok) {
        setSelectedOrder(data.data)
        setShowViewModal(true)
      } else {
        setError('Error al cargar detalles de la orden')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error fetching order details:', err)
    }
  }

  // Open create modal
  const openCreateModal = () => {
    setEditingOrder(null)
    setFormData({
      supplier_id: '',
      notes: '',
      items: []
    })
    setShowCreateModal(true)
    setError(null)
  }

  // Edit order
  const editOrder = async (orderId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/purchase-orders/${orderId}`)
      const data = await response.json()

      if (response.ok && data.data) {
        const order = data.data
        setEditingOrder(order)
        setFormData({
          supplier_id: order.supplier_id || '',
          notes: order.notes || '',
          items: order.items?.map((item: any) => ({
            ingredient_id: item.ingredient?.id || item.ingredient_id,
            quantity: parseFloat(parseFloat(String(item.quantity)).toFixed(2)),
            unit_id: item.unit?.id || item.unit_id,
            unit_price: item.unit_price ? parseFloat(parseFloat(String(item.unit_price)).toFixed(2)) : 0,
            notes: item.notes || ''
          })) || []
        })
        setShowCreateModal(true)
        setError(null)
      } else {
        setError('Error al cargar datos de la orden')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error loading order for edit:', err)
    }
  }

  // Duplicate order
  const duplicateOrder = async (orderId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/purchase-orders/${orderId}/duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const data = await response.json()

      if (response.ok) {
        await fetchOrders()
        setError(null)
      } else {
        setError(data.message || 'Error al duplicar la orden')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error duplicating order:', err)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Órdenes de Compra</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestiona las órdenes de compra a proveedores
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Nueva Orden
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por código o proveedor..."
              className="pl-10 w-full rounded-md border-gray-300 text-sm"
            />
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Estado:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border-gray-300 text-sm"
            >
              <option value="all">Todos</option>
              <option value="draft">Borrador</option>
              <option value="sent">Enviadas</option>
              <option value="partial">Parcial</option>
              <option value="received">Recibidas</option>
              <option value="closed">Cerradas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <ShoppingCartIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay órdenes de compra</h3>
          <p className="mt-1 text-sm text-gray-500">Comienza creando una nueva orden.</p>
          <div className="mt-6">
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Nueva Orden
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Código" field="code" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Proveedor" field="supplier.trade_name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Items" field="items_count" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Estado" field="status" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Fecha" field="created_at" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <th className="relative px-6 py-3">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {order.code}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {order.supplier?.trade_name || 'Sin proveedor'}
                    </div>
                    {order.supplier?.nif && (
                      <div className="text-xs text-gray-500">
                        NIF: {order.supplier.nif}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {order.items_count || 0} items
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(order.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {fmtDate(order.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => viewOrderDetails(order.id)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Ver detalles"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => duplicateOrder(order.id)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Duplicar orden"
                      >
                        <DocumentDuplicateIcon className="h-5 w-5" />
                      </button>
                      {order.status === 'draft' && (
                        <>
                          <button
                            onClick={() => editOrder(order.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Editar orden"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => sendOrder(order.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Enviar orden"
                          >
                            <PaperAirplaneIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {['draft', 'sent'].includes(order.status) && (
                        <button
                          onClick={() => deleteOrder(order.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Página {currentPage} de {totalPages} ({filteredOrders.length} órdenes)
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Order Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-4xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingOrder ? 'Editar Orden de Compra' : 'Nueva Orden de Compra'}
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Proveedor *
                    </label>
                    <select
                      value={formData.supplier_id}
                      onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      required
                    >
                      <option value="">Seleccionar proveedor...</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.trade_name} - {supplier.nif}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Notas
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Notas adicionales..."
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Items de la orden
                      </label>
                      <button
                        type="button"
                        onClick={addItem}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Agregar Item
                      </button>
                    </div>

                    {formData.items.length === 0 ? (
                      <div className="text-center py-4 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-500">
                          No hay items agregados. Haz clic en "Agregar Item" para comenzar.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {formData.items.map((item, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded-md">
                            <div className="grid grid-cols-6 gap-2">
                              <div className="col-span-2">
                                <select
                                  value={item.ingredient_id}
                                  onChange={(e) => updateItem(index, 'ingredient_id', parseInt(e.target.value))}
                                  className="w-full text-sm border-gray-300 rounded-md"
                                  required
                                >
                                  <option value={0}>Seleccionar ingrediente</option>
                                  {ingredients.map((ing) => (
                                    <option key={ing.id} value={ing.id}>
                                      {ing.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value))}
                                  placeholder="Cantidad"
                                  className="w-full text-sm border-gray-300 rounded-md"
                                  min="0.5"
                                  step="0.5"
                                />
                              </div>
                              <div>
                                <select
                                  value={item.unit_id}
                                  onChange={(e) => updateItem(index, 'unit_id', parseInt(e.target.value))}
                                  className="w-full text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                                  required
                                  disabled={item.ingredient_id > 0}
                                >
                                  <option value={0}>Unidad</option>
                                  {units.map((unit) => (
                                    <option key={unit.id} value={unit.id}>
                                      {unit.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <input
                                  type="number"
                                  value={item.unit_price || ''}
                                  onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || null)}
                                  placeholder="Precio/unidad"
                                  className="w-full text-sm border-gray-300 rounded-md"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                              <div className="flex items-center">
                                <button
                                  type="button"
                                  onClick={() => removeItem(index)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                            <div className="mt-2">
                              <input
                                type="text"
                                value={item.notes || ''}
                                onChange={(e) => updateItem(index, 'notes', e.target.value)}
                                placeholder="Notas del item..."
                                className="w-full text-sm border-gray-300 rounded-md"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleCreateOrder}
                  disabled={saving || formData.items.length === 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {saving ? 'Guardando...' : (editingOrder ? 'Actualizar Orden' : 'Crear Orden')}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Order Modal */}
      {showViewModal && selectedOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-4xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Detalles de Orden {selectedOrder.code}
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Proveedor</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedOrder.supplier?.trade_name || 'Sin proveedor'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Estado</p>
                      <div className="mt-1">
                        {getStatusBadge(selectedOrder.status)}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Fecha Creación</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {fmtDateTime(selectedOrder.created_at)}
                      </p>
                    </div>
                    {selectedOrder.sent_at && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Fecha Envío</p>
                        <p className="mt-1 text-sm text-gray-900">
                          {fmtDateTime(selectedOrder.sent_at)}
                        </p>
                      </div>
                    )}
                  </div>

                  {selectedOrder.notes && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Notas</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedOrder.notes}</p>
                    </div>
                  )}

                  {selectedOrder.items && selectedOrder.items.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Items de la orden</p>
                      <div className="border rounded-md overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Ingrediente
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Cantidad
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Unidad
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Precio/Unidad
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Notas
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {selectedOrder.items.map((item, index) => (
                              <tr key={index}>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {item.ingredient?.name || 'N/A'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {parseFloat(String(item.quantity)).toFixed(2)}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {item.unit?.name || 'N/A'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {item.unit_price ? `€${parseFloat(String(item.unit_price)).toFixed(2)}` : '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {item.notes || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:gap-3">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                >
                  Cerrar
                </button>
                {['draft', 'sent'].includes(selectedOrder.status) && (
                  <button
                    onClick={printPurchaseList}
                    disabled={printing}
                    className="mt-3 sm:mt-0 w-full inline-flex justify-center items-center gap-2 rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                  >
                    <PrinterIcon className="h-5 w-5" />
                    {printing ? 'Imprimiendo…' : 'Imprimir lista de compra'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}